import React, { ReactNode, useState } from "react";
import { Collapse, List, Row, Col, Checkbox, Typography, Input, Tooltip } from "antd";
import "./matter-editor-pricing.module.css";
import {
  Phase,
  Discount,
  DiscountType,
  Disbursement,
  Uplift,
  Work,
  PricingOptions,
  PricingLevel,
  PricingType,
  PriceRange,
  UpliftType,
  pricingFieldsByType,
  pricingFieldsByTypeFlattened,
  TaxLabelStatus,
  TechnologyCharge,
  TechnologyChargeType,
} from "../../data-types";
import { ListItem, ItemProps, ListItemEditable, ListItemEditableRange } from "./list-item";
import { PricingTotals } from "./pricing-totals";
import { createTotalsText } from "./pricing-text-creators";
import { sumWorkPriceSingle, sumWorkPriceRange } from "./sum-legal-fee-costs";
import { getTechnologyChargeAmount } from "./sum-technology-charges";
import { UpliftTooltip } from "./uplift-tooltip";

import { useDebouncedCallback } from "use-debounce";
import { TaxLabel } from "~/gql/graphql";

const { Panel } = Collapse;

interface HeaderProps extends ItemProps {
  strikethrough?: boolean;
}

const Header: React.FC<HeaderProps> = ({ title, value, className, strikethrough }) => {
  return (
    <Row justify="space-between" wrap={false} className={className}>
      <Col flex="1 1 auto">{title}</Col>
      <Col flex="0 0 auto">{strikethrough ? <del>{value}</del> : value}</Col>
    </Row>
  );
};

const UpliftHeader: React.FC<ItemProps> = ({ title, value, className }) => {
  return (
    <Row justify="space-between" wrap={false} className={className}>
      <Col flex="0 1 auto" style={{ marginRight: 8 }}>
        {title}
      </Col>
      <Col flex="1 0 auto">{<UpliftTooltip></UpliftTooltip>}</Col>
      <Col flex="0 0 auto">{value}</Col>
    </Row>
  );
};

interface Props {
  template?: boolean;
  phases: Phase[] | null;
  uplifts: Uplift[] | null;
  discounts: Discount[] | null;
  disbursements: Disbursement[] | null;
  technologyCharges: TechnologyCharge[] | null;
  matterWork: Work[] | null;
  topDownRateDiscount: number | null;
  pricingOptions: PricingOptions;
  footer: ReactNode;
  pricingType: PricingType;
  pricingLevel: PricingLevel;
  onUpdatePrice: (phaseId: number, data: Partial<PricingOptions>) => void;
  excludeTax: boolean;
  taxLevelPercent: number;
  taxLabel: TaxLabel;
  administrationCharge: number;
  waiveAdminCharge: boolean;
  waiveAdminChargeReason: string | null;
  onWaiveAdminChargeChange: (value: boolean, reason?: string) => void;
  currencySymbol: string;
  useTechPricing: boolean;
}

const ascendingOrderComparator = (a: { order: number }, b: { order: number }) => a.order - b.order;
export const MatterEditorPricing: React.FC<Props> = ({
  template,
  phases,
  uplifts,
  discounts,
  disbursements,
  technologyCharges,
  matterWork,
  pricingOptions,
  footer,
  pricingType,
  pricingLevel,
  onUpdatePrice,
  excludeTax,
  taxLevelPercent,
  taxLabel,
  administrationCharge,
  waiveAdminCharge,
  waiveAdminChargeReason,
  onWaiveAdminChargeChange,
  currencySymbol,
  useTechPricing,
}) => {
  const [waiverReason, setWaiverReason] = useState<string | null>(waiveAdminChargeReason);
  if (pricingType === PricingType.None) return null;

  const delayOnUpdatePrice = useDebouncedCallback(onUpdatePrice, 750);

  const {
    legalFees,
    technologyChargesTotal,
    discountsTotal,
    disbursementsTotal,
    upliftsTotal,
    adminChargeTotal,
    subTotalExcludingVat,
    vat,
    subTotalIncludingVat,
    grandTotal,
  } = createTotalsText(
    pricingOptions,
    phases,
    disbursements,
    uplifts,
    discounts,
    technologyCharges,
    matterWork,
    pricingLevel,
    pricingType,
    excludeTax,
    taxLevelPercent,
    administrationCharge,
    waiveAdminCharge,
    currencySymbol
  );
  const totals = [
    ...(!excludeTax
      ? [
          {
            title: `Subtotal (excl. ${taxLabel})`,
            value: subTotalExcludingVat,
          },
          { title: `${taxLabel} ${taxLevelPercent}%`, value: vat },
          {
            title: `Subtotal (incl. ${taxLabel})`,
            value: subTotalIncludingVat,
          },
        ]
      : [
          {
            title: "Subtotal",
            value: subTotalIncludingVat,
          },
        ]),
    ...(template
      ? []
      : [
          {
            title: "Disbursements",
            value: disbursementsTotal,
          },
        ]),
    { title: "Total", value: grandTotal },
  ];
  return (
    <div>
      <Collapse expandIconPosition="right">
        <Panel header={<Header title={"Legal Fees"} value={legalFees} />} key={"Legal Fees"}>
          <List
            dataSource={
              phases
                ?.map((p) => ({
                  id: p.id,
                  title: <div dangerouslySetInnerHTML={{ __html: p.title }} />,
                  pricing: pricingFieldsByType(pricingType, p.priceOptions),
                  calculated:
                    pricingType === PricingType.EstimatedRange
                      ? sumWorkPriceRange(p.work)
                      : sumWorkPriceSingle(p.work),
                  order: p.order,
                }))
                .sort(ascendingOrderComparator) ?? []
            }
            renderItem={(item) => {
              if (pricingLevel === PricingLevel.FeeEarner) {
                return pricingType === PricingType.EstimatedRange ? (
                  <ListItemEditableRange
                    currencySymbol={currencySymbol}
                    title={item.title}
                    chosen={item.pricing.chosen as PriceRange}
                    calculated={item.calculated as PriceRange}
                    onChangeWithDelay={(value) =>
                      delayOnUpdatePrice(item.id, {
                        estimatedRangeChosenLower: value.lower,
                        estimatedRangeChosenUpper: value.upper,
                      })
                    }
                    onChange={(value) =>
                      onUpdatePrice(item.id, {
                        estimatedRangeChosenLower: value.lower,
                        estimatedRangeChosenUpper: value.upper,
                      })
                    }
                  />
                ) : (
                  <ListItemEditable
                    currencySymbol={currencySymbol}
                    title={item.title}
                    chosen={item.pricing.chosen as number | null}
                    calculated={item.calculated as number | null}
                    onChangeWithDelay={(value) => {
                      // value is number
                      const transformed = pricingFieldsByTypeFlattened(
                        pricingType,
                        pricingOptions,
                        value,
                        "chosen"
                      );
                      // pricingType : pricingType
                      delayOnUpdatePrice(item.id, transformed);
                    }}
                    onChange={(value) => {
                      // value is number
                      const transformed = pricingFieldsByTypeFlattened(
                        pricingType,
                        pricingOptions,
                        value,
                        "chosen"
                      );
                      // pricingType : pricingType
                      onUpdatePrice(item.id, transformed);
                    }}
                  />
                );
              } else {
                return (
                  <ListItem
                    title={item.title}
                    value={
                      pricingLevel === PricingLevel.Matter
                        ? ""
                        : pricingType === PricingType.EstimatedRange
                        ? `${currencySymbol} ${
                            (item.pricing.chosen as unknown as PriceRange).lower || 0
                          } to ${currencySymbol} ${
                            (item.pricing.chosen as unknown as PriceRange).upper || 0
                          }`
                        : `${currencySymbol} ${item.pricing.chosen || 0}`
                    }
                  />
                );
              }
            }}
          />
        </Panel>
        {!template && (
          <Panel header={<UpliftHeader title={"Uplifts"} value={upliftsTotal} />} key={"Uplifts"}>
            <List
              dataSource={uplifts
                ?.map((a) => ({
                  title: a.baseUplift?.reason || "",
                  value: `+${a.type === UpliftType.Amount ? currencySymbol : ""}${a.amount}${
                    a.type === UpliftType.Percent ? "%" : ""
                  }`,
                  order: a.order,
                }))
                .sort(ascendingOrderComparator)}
              renderItem={(item) => <ListItem title={item.title} value={item.value} />}
            />
          </Panel>
        )}
        {!template && (
          <Panel header={<Header title={"Discounts"} value={discountsTotal} />} key={"Discounts"}>
            <List
              dataSource={discounts
                ?.map((d) => ({
                  title: d.reason,
                  value:
                    d.type === DiscountType.Percent
                      ? `-${d.amount}%`
                      : `-${currencySymbol}${d.amount}`,
                  order: d.order,
                }))
                .sort(ascendingOrderComparator)}
              renderItem={(item) => <ListItem title={item.title} value={item.value} />}
            />
          </Panel>
        )}
        {!template && administrationCharge > 0 && (
          <Panel
            header={
              <Header
                title={"Administration Charge"}
                value={adminChargeTotal}
                strikethrough={waiveAdminCharge}
              />
            }
            key={"Administration Charge"}
          >
            <List.Item>
              <Typography.Text ellipsis={true} style={{ marginRight: "100px" }}>
                Administration Charge
              </Typography.Text>
              {waiveAdminCharge ? (
                <>
                  <span
                    style={{ textDecoration: "line-through" }}
                  >{`${administrationCharge}%`}</span>
                  <span>0%</span>
                </>
              ) : (
                <span>{`${administrationCharge}%`}</span>
              )}
            </List.Item>
            <Input
              placeholder="Enter reason for waiver"
              value={waiverReason ?? ""}
              onChange={(e) => setWaiverReason(e.target.value)}
              disabled={waiveAdminCharge}
              style={{ marginBottom: 8 }}
            />
            {!waiverReason && !waiveAdminCharge ? (
              <Tooltip title="Please add a reason for waiving the admin charge.">
                <Checkbox
                  checked={waiveAdminCharge}
                  onChange={(e) => onWaiveAdminChargeChange(e.target.checked)}
                  disabled={true}
                >
                  Waive admin charge?
                </Checkbox>
              </Tooltip>
            ) : (
              <Checkbox
                checked={waiveAdminCharge}
                onChange={(e) =>
                  onWaiveAdminChargeChange(e.target.checked, waiverReason ?? undefined)
                }
              >
                Waive admin charge?
              </Checkbox>
            )}
          </Panel>
        )}
        {!template && useTechPricing && (
          <Panel
            header={<Header title={"Technology Charges"} value={technologyChargesTotal} />}
            key={"Technology Charges"}
          >
            <List
              dataSource={technologyCharges?.map((tc) => ({
                title: tc.description,
                value: `${tc.type === TechnologyChargeType.Percentage ? "+" : currencySymbol}${
                  tc.type === TechnologyChargeType.Percentage
                    ? tc.percentage
                    : getTechnologyChargeAmount(tc, 0)
                }${tc.type === TechnologyChargeType.Percentage ? "%" : ""}`,
              }))}
              renderItem={(item) => <ListItem title={item.title} value={item.value} />}
            />
          </Panel>
        )}
        <Panel
          header={<Header title={"Disbursements"} value={disbursementsTotal} />}
          key={"Disbursements"}
        >
          <List
            dataSource={disbursements
              ?.map((d) => ({
                title: d.name,
                value: `${currencySymbol}${d.amount}`,
                amount: d.amount,
                order: d.order,
                vatStatus: d.vatStatus,
              }))
              .sort(ascendingOrderComparator)}
            renderItem={(item) => {
              const taxAmount = Math.round((taxLevelPercent / 100) * item.amount * 100) / 100;
              return (
                <ListItem
                  title={
                    excludeTax
                      ? item.title
                      : getTaxLabelStatusText(
                          item.title,
                          item.vatStatus,
                          taxLabel,
                          currencySymbol,
                          taxAmount
                        )
                  }
                  value={item.value}
                />
              );
            }}
          />
        </Panel>
      </Collapse>
      <PricingTotals data={totals} />
      {footer}
    </div>
  );
};

const getTaxLabelStatusText = (
  title: string,
  taxLabelStatus: TaxLabelStatus,
  taxLabel: TaxLabel,
  currencySymbol: string,
  taxAmount: number
) => {
  return `${title} ${
    taxLabelStatus === TaxLabelStatus.Inclusive
      ? `(incl. ${taxLabel})`
      : taxLabelStatus === TaxLabelStatus.Exclusive
      ? `(excl. ${taxLabel} of ${currencySymbol}${taxAmount})`
      : `(${taxLabel} exempt)`
  }`;
};
